import React, { useState } from "react";
import { CalendarClock, Check, Clock, X } from "lucide-react";
import { ChannelBadge } from "@/components/ChannelBadge";
import { followupApi, apiErrorMessage } from "@/api/client";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  snoozed: { label: "Snoozed", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  dismissed: { label: "Dismissed", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  auto_suppressed: { label: "Auto-suppressed", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  suppressed: { label: "Suppressed", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

/** Renders a visit's deterministic follow-up suggestion with Accept / Snooze / Dismiss. */
export function FollowUpCard({ visit, canAct = true, onAction }) {
  const sna = visit.suggested_next_action;
  const [busy, setBusy] = useState(false);
  if (!sna) return null;

  const st = STATUS_META[sna.status] || STATUS_META.pending;
  const isTask = sna.action === "task";
  const terminal = ["dismissed", "suppressed"].includes(sna.status);
  const act = async (kind) => {
    setBusy(true);
    try {
      await followupApi[kind](visit.id);
      toast.success(`Follow-up ${kind === "accept" ? "accepted" : kind === "snooze" ? "snoozed 7 days" : "dismissed"}`);
      onAction?.();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`followup-card-${visit.id}`} className="rounded-xl border border-teal-100 bg-teal-50/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-teal-700">
          <CalendarClock className="h-3.5 w-3.5" /> Follow-up
        </span>
        <span data-testid={`followup-status-${visit.id}`} className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${st.cls}`}>{st.label}</span>
      </div>
      <p className="mt-1.5 text-sm font-medium text-slate-700">{sna.reason}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        {isTask && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Due {fmtDate(sna.due_date)}</span>}
        {sna.channel && <ChannelBadge channel={(sna.channel || "wait").toUpperCase()} reasons={sna.channel_reasons} testid={`followup-channel-${visit.id}`} />}
      </div>
      {sna.auto_suppressed_reason && <p className="mt-1 text-[11px] font-medium text-slate-400">{sna.auto_suppressed_reason}</p>}
      {canAct && isTask && !terminal && (
        <div className="mt-2.5 flex gap-2">
          <button disabled={busy} data-testid={`followup-accept-${visit.id}`} onClick={() => act("accept")}
            className="inline-flex items-center gap-1 rounded-md bg-teal-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-60"><Check className="h-3.5 w-3.5" /> Accept</button>
          <button disabled={busy} data-testid={`followup-snooze-${visit.id}`} onClick={() => act("snooze")}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"><Clock className="h-3.5 w-3.5" /> Snooze 7d</button>
          <button disabled={busy} data-testid={`followup-dismiss-${visit.id}`} onClick={() => act("dismiss")}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-50 disabled:opacity-60"><X className="h-3.5 w-3.5" /> Dismiss</button>
        </div>
      )}
    </div>
  );
}
